<script setup>
import { ref, computed } from "vue";
import { Inertia } from '@inertiajs/inertia';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import { Link, Head } from '@inertiajs/inertia-vue3';
import PremiumPageHeader from '@/Components/PremiumPageHeader.vue';
import { Icon } from "@iconify/vue";
import { mdiPlusCircle, mdiPencil } from "@mdi/js";
import CardBox from "@/Components/CardBox.vue";
import TableFilter from "@/Components/TableFilter.vue";
import CardBoxComponentEmpty from '@/Components/CardBoxComponentEmpty.vue';
import Buttons from "@/Components/Buttons.vue";
import Dropdown from "@/Components/Dropdown.vue";
import DropdownLink from "@/Components/DropdownLink.vue";
import SelectSearch from "@/Components/SelectSearch.vue";

const props = defineProps({
    students: [Array, Object],
    columnas: [Array, Object],
    classrooms: [Array, Object],
    teoricclassrooms: [Array, Object],
    levels: [Array, Object],
    levelsparent: [Array, Object],
    levelsvacacional: [Array, Object],
    classroomsvacacional: [Array, Object],
    sedes: [Array, Object],
    contactos: [Array, Object],
    isAdmin: Boolean,
    isCoord: Boolean,
    statusOptions: [Array, Object],
    userParam: [Array, Object]
});
//console.log(props.sedes)
const rutaPrefijo = ref('student');
const modulo = ref('estudiante');
const modulos = ref('Estudiantes');

const massaction = ref('');
const isProcessingMassAction = ref(false);
const massiveFieldValue = ref('');

const executeMassAction = async (selectedItems, clearSelection, notify) => {
    const arrayitemschecks = selectedItems.map(item => item.id_person); // <-- Id de objeto para la acción masiva, en este caso es id_persona

    let id_select1 = null;
    let id_select2 = null;
    let id_select3 = null;
    let id_select4 = null;

    if (massaction.value === 'tipo_stdt' || massaction.value === 'changeSelect1') {
        id_select1 = massiveFieldValue.value;
    } else if (massaction.value === 'changeSelect2') {
        id_select2 = massiveFieldValue.value;
    } else if (massaction.value === 'changeSelect3') {
        id_select3 = massiveFieldValue.value;
    } else if (massaction.value === 'changeSelect4') {
        id_select4 = massiveFieldValue.value;
    }

    const formMassAction = {
        'action': massaction.value,
        'cr': arrayitemschecks,
        'modulo': modulo.value,
        'origin_type': '',
        'field_value': massiveFieldValue.value,
        'id_select1': id_select1,
        'id_select2': id_select2,
        'id_select3': id_select3,
        'id_select4': id_select4,
    };

    isProcessingMassAction.value = true;
    try {
        const response = await axios.put('/' + rutaPrefijo.value + '/massaction', formMassAction);
        const respuesta = response.data;
        if (respuesta.success) {
            notify(respuesta.message || 'Registros actualizados correctamente', 'success');
            clearSelection();
            massaction.value = '';
            massiveFieldValue.value = '';
            Inertia.reload({ only: ['students'] });
        } else {
            notify(respuesta.message || 'Error al procesar la acción masiva', 'danger');
        }
    } catch (error) {
        console.error(error);
        notify('Ocurrió un error en el servidor. Revise la consola.', 'danger');
    } finally {
        isProcessingMassAction.value = false;
    }
};

const mapOptionsLocal = (obj) => {
    if (!obj) return undefined;
    if (Object.keys(obj).length === 0) return undefined;
    if (Array.isArray(obj)) {
        if (obj.length > 0 && typeof obj[0] !== 'object') {
            return obj.map((value, index) => ({ value: index, label: value }));
        }
        return obj.map(o => ({ value: isNaN(o.value) ? o.value : Number(o.value), label: o.label }));
    }
    return Object.entries(obj).map(([key, value]) => ({
        value: isNaN(key) ? key : Number(key),
        label: value
    }));
};

const columns = computed(() => {
    const baseColumns = [
        { key: 'actions', label: 'Acciones', align: 'center' },
    ];

    if (props.isAdmin) {
        baseColumns.push(
            { key: 'apellidos', label: 'Apellidos', 'check': true, linkObject: '/student/card?id=__ID__' },
            { key: 'nombres', label: 'Nombres', 'check': true },
            { key: 'num_doc', label: 'Documento', align: 'center' },
            { key: 'ref_ugel', label: 'Código UGEL' },
            { key: 'celular', label: 'Celular', fieldType: 'mobile_phone', align: 'between' },
            { key: 'type', label: 'Tipo', filterType: 'select', filterOptions: [{ value: 0, label: 'Nuevo' }, { value: 1, label: 'Antiguo' }] },
            { key: 'id_level_parent', label: 'Nivel General', filterType: 'select', filterOptions: mapOptionsLocal(props.levelsparent) },
            { key: 'id_level', label: 'Nivel/Grado', filterType: 'select', filterOptions: mapOptionsLocal(props.levels) },
            { key: 'id_classroom', label: 'Aula Estudio', filterType: 'select-search', filterOptions: mapOptionsLocal(props.classrooms) },
            { key: 'id_classroom_matricula', label: 'Aula Matrícula', filterType: 'select-search', filterOptions: mapOptionsLocal(props.classrooms) },
            { key: 'id_classroom_ugel', label: 'Aula UGEL', filterType: 'select-search', filterOptions: mapOptionsLocal(props.teoricclassrooms) },
            { key: 'id_level_vacacional', label: 'Nivel Vacacional', filterType: 'select', filterOptions: mapOptionsLocal(props.levelsvacacional) },
            { key: 'id_classroom_vacacional', label: 'Aula Vacacional', filterType: 'select-search', filterOptions: mapOptionsLocal(props.classroomsvacacional) },
            { key: 'sede_origen', label: 'Sede Origen', filterType: 'select', filterOptions: mapOptionsLocal(props.sedes) },
            { key: 'fecha_matricula', label: 'Fecha Matrícula', filterType: 'date', align: 'center' },
            { key: 'fecha_matricula_vacacional', label: 'Matrícula Vac.', filterType: 'date', align: 'center' },
            { key: 'becado', label: 'Becado', filterType: 'select', filterOptions: [{ value: 0, label: 'No' }, { value: 1, label: 'Si' }] },
            { key: 'pension_preferencial', label: 'Pensión Pref.' },
            { key: 'fecha_compromiso_pago', label: 'Comp. Pago', filterType: 'date', align: 'center' },
            { key: 'id_student_contact', label: 'Apoderado/RP', filterOptions: mapOptionsLocal(props.contactos) },
            { key: 'status_user', label: 'Acceso', filterType: 'select', isBoolean: true, booleanLabels: { true: 'Activo', false: 'Inactivo' }, align: 'center' },
            { key: 'status', label: 'Estado', filterType: 'select', filterOptions: mapOptionsLocal(props.statusOptions), filterColor: [{ value: -2, color: 'violet' }, { value: -1, color: 'stone' }, { value: 0, color: 'red' }, { value: 1, color: 'green' }, { value: 2, color: 'amber' }, { value: 3, color: 'gray' }, { value: 4, color: 'blue' }], align: 'center', filterDefault: '1' }
        );
    } else {
        baseColumns.push(
            { key: 'apellidos', label: 'Apellidos', linkObject: '/student/card?id=__ID__' },
            { key: 'nombres', label: 'Nombres' },
            { key: 'num_doc', label: 'Documento', align: 'center' },
            { key: 'ref_ugel', label: 'Código UGEL' },
            { key: 'celular', label: 'Celular', fieldType: 'mobile_phone' },
            { key: 'type', label: 'Tipo', filterType: 'select', filterOptions: [{ value: 0, label: 'Nuevo' }, { value: 1, label: 'Antiguo' }] },
            { key: 'id_level_parent', label: 'Nivel Padre', filterType: 'select', filterOptions: mapOptionsLocal(props.levelsparent) },
            { key: 'id_level', label: 'Nivel/Grado', filterType: 'select', filterOptions: mapOptionsLocal(props.levels) },
            { key: 'id_classroom', label: 'Aula Estudio', filterType: 'select-search', filterOptions: mapOptionsLocal(props.classrooms) },
            { key: 'id_classroom_matricula', label: 'Aula Matrícula', filterType: 'select-search', filterOptions: mapOptionsLocal(props.classrooms) },
            { key: 'id_classroom_ugel', label: 'Aula UGEL', filterType: 'select-search', filterOptions: mapOptionsLocal(props.teoricclassrooms) },
            { key: 'id_level_vacacional', label: 'Nivel Vacacional', filterType: 'select', filterOptions: mapOptionsLocal(props.levelsvacacional) },
            { key: 'id_classroom_vacacional', label: 'Aula Vacacional', filterType: 'select-search', filterOptions: mapOptionsLocal(props.classroomsvacacional) },
            { key: 'sede_origen', label: 'Sede Origen', filterType: 'select', filterOptions: mapOptionsLocal(props.sedes) },
            { key: 'fecha_matricula', label: 'Fecha Matrícula' },
            { key: 'fecha_matricula_vacacional', label: 'Matrícula Vacacional' },
            { key: 'id_student_contact', label: 'Apoderado/RP', filterOptions: mapOptionsLocal(props.contactos) },
            { key: 'status', label: 'Estado', filterType: 'select', filterOptions: mapOptionsLocal(props.statusOptions), filterColor: [{ value: -2, color: 'violet' }, { value: -1, color: 'stone' }, { value: 0, color: 'red' }, { value: 1, color: 'green' }, { value: 2, color: 'amber' }, { value: 3, color: 'gray' }, { value: 4, color: 'blue' }], align: 'center', filterDefault: '1', }
        );
    }
    return baseColumns;
});

// LISTA DE OPCIONES PARA EL LLAMADO DE CONTENIDO EN LA TABLA
// Para determinar el titulo de la columna, ejemplo label: 'Mi titulo'.
// Para establecer alineación del contenido usar el atributo, por ejemplo; align: 'center'
// Para habilitar un filtro tipo select; filterType: 'select',
// Si el contenido a mostrar ese select y booleano; isBoolean: true . Y establecer las etiquetas por ejemplo; booleanLabels: { true: 'Activo', false: 'Inactivo' },
// Si se desea establecer un filtro select con varias opciones personalizadas; filterType: 'select', filterOptions: [{ value: 1, label: 'Maestro' }, { value: 2, label: 'Administrador' }, { value: 3, label: 'Directivo' }],
// Si es requerido el contenido a mostrar tenga color, establecer en filter option el atributo de color, ejemplo; { value: 0, label: 'pendiente', color: 'red' }
// filterOptions: mapOptionsLocal(props.statusOptions) valido para traer los items desde el controllador
// filterColor: [{ value: 0, color: 'gray'}, valido para establecer el color de los items, solo si se usa filterOptions: mapOptionsLocal
// filterType: 'date' para establecer un filtro de fecha con 2 input

// Valor por defecto en select, como ejemplo; filterDefault: '1'
// Si se requiere usar Select con buscado, establecer el atributo filterType: 'select-search'

// COMPONENTE TABLEFILTER, OPCIONES ADICIONALES
// Para desactivar el filtro de cada columna y activar un filtro general; simple-search = true
// Para activar la barra de desplazamiento superior (Habran 2 barras, arriba y abajo); top-scrollbar = true
// Para establecer las columnas que se muestran por defecto; userParam = true o :userParam="props.userParam"
// checkable = true - Para activar la seleccion de filas
// Para activar la exportacion de datos; exporteable = true
// Para activar modal de confirmación requestConfirm en #mass-actions de TableFilter / Template
// manage-columns da la posibilidad de guardar las columnas que se muestran por defecto; :ruta="rutaPrefijo" determina el nombre en la base de datos

</script>

<template>

    <Head title="Estudiantes" />
    <BreezeAuthenticatedLayout>
        <template #header>
            <PremiumPageHeader title="Estudiantes" subtitle="Gestión y control de alumnos matriculados" icon="tabler:users" color="indigo" :actionsOnBottomOnMobile="false">
                <template #actions>
                    <Buttons label="Nuevo Estudiante" icon="f7:plus-app-fill" size="vuexy" color="blue" :href="route(rutaPrefijo + '-create')" :hideLabelOnMobile="true" />
                </template>
            </PremiumPageHeader>
        </template>

        <div>
            <div class="card-premium p-6">
                <div class="mb-6 flex items-center gap-3 p-4 bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-100/80 dark:border-indigo-900/50 rounded-xl text-sm text-indigo-800 dark:text-indigo-300">
                    <Icon icon="tabler:info-circle" class="w-5 h-5 shrink-0 text-indigo-500 dark:text-indigo-400" />
                    <span>Por defecto se muestran estudiantes en <strong>Estado Activo</strong>.</span>
                </div>

                <div v-if="columnas.length" class="bg-white dark:bg-slate-800">
                    <div class="w-full relative">
                        <!-- Codigo desechado; :extra-params="{ existecols: 1 }" -->
                        <TableFilter :data="students" :columns="columns" title="Alumnos" :ruta="rutaPrefijo" :modulo="modulo" :userParam="userParam" top-scrollbar manage-columns checkable exporteable :processing="isProcessingMassAction">
                            <template #mass-actions="{ selectedItems, clearSelection, requestConfirm, notify }">
                                <div class="flex flex-col md:flex-row sm:items-center gap-3 p-1 w-full xl:w-auto">
                                    <select v-model="massaction" class="w-full xl:w-auto input-premium" :disabled="isProcessingMassAction">
                                        <option value="" disabled>Acción masiva...</option>
                                        <option v-if="props.isAdmin" value="enabled">Habilitar Acceso</option>
                                        <option v-if="props.isAdmin" value="disabled">Deshabilitar Acceso</option>
                                        <option v-if="props.isAdmin" value="activar">Establecer a Activo</option>
                                        <option v-if="props.isAdmin" value="ingresante">Establecer a Ingresante</option>
                                        <option v-if="props.isAdmin" value="egresado">Establecer a Egresado</option>
                                        <option v-if="props.isAdmin" value="retirado">Establecer a Retirado (Trasladado)</option>
                                        <option v-if="props.isAdmin" value="externo">Establecer a Externo (O. Sede/Inst.)</option>
                                        <option v-if="props.isAdmin" value="pensionpref">Establecer Pensión Preferencial</option>
                                        <option v-if="props.isAdmin" value="compromisopago">Establecer Compromiso de Pago</option>
                                        <option v-if="props.isAdmin" value="tipo_stdt">Establecer Tipo Estudiante</option>
                                        <option value="changeSelect1">Cambiar Aula de Estudio</option>
                                        <option value="changeSelect2">Cambiar Aula Matrícula</option>
                                        <option value="changeSelect3">Cambiar Aula Ugel</option>
                                        <option value="changeSelect4">Cambiar aula estudio y matrícula</option>
                                    </select>
                                    <div v-if="massaction === 'pensionpref'" class="w-full xl:w-auto">
                                        <input type="number" v-model="massiveFieldValue" class="w-full sm:min-w-[175px] input-premium" placeholder="Monto">
                                    </div>
                                    <div v-if="massaction === 'compromisopago'" class="w-full xl:w-auto">
                                        <input type="date" v-model="massiveFieldValue" class="w-full sm:min-w-[175px] input-premium">
                                    </div>
                                    <div v-if="massaction === 'tipo_stdt'" class="w-full xl:w-auto">
                                        <select v-model="massiveFieldValue" class="w-full xl:w-auto input-premium">
                                            <option value="" disabled>Seleccione el tipo...</option>
                                            <option value="0">Nuevo</option>
                                            <option value="1">Antiguo</option>
                                        </select>
                                    </div>
                                    <div v-if="massaction === 'changeSelect1' || massaction === 'changeSelect2' || massaction === 'changeSelect4'" class="w-full xl:w-auto">
                                        <SelectSearch v-model="massiveFieldValue" :options="mapOptionsLocal(props.classrooms)" placeholder="Seleccione el aula..." />
                                    </div>
                                    <div v-if="massaction === 'changeSelect3'" class="w-full xl:w-auto">
                                        <SelectSearch v-model="massiveFieldValue" :options="mapOptionsLocal(props.teoricclassrooms)" placeholder="Seleccione el aula ugel..." />
                                    </div>
                                    <div class="flex w-full xl:w-auto gap-2">
                                        <Buttons @click="!massaction ? alert('Por favor, selecciona una acción masiva.') : requestConfirm(`¿Está seguro de aplicar esta acción masiva a los <b>${selectedItems.length}</b> seleccionados?`, () => executeMassAction(selectedItems, clearSelection, notify))" color="blue" size="sm" class="flex-1 justify-center whitespace-nowrap" :class="{ 'opacity-50 cursor-not-allowed': (!massaction || isProcessingMassAction) }">
                                            <i class="fa-solid" :class="isProcessingMassAction ? 'fa-spinner fa-spin' : 'fa-check'"></i>
                                            <span class="ml-1">Confirmar</span>
                                        </Buttons>
                                        <Buttons @click="clearSelection(); massaction = ''" color="red" size="sm" class="flex-1 justify-center whitespace-nowrap" :disabled="isProcessingMassAction">
                                            <i class="fa-solid fa-xmark"></i>
                                            <span class="ml-1">Cancelar</span>
                                        </Buttons>
                                    </div>
                                </div>
                            </template>
                            <template #cell-actions="{ item }">
                                <div class="flex flex-row justify-center gap-2">
                                    <Dropdown align="Right" width="48">
                                        <template #trigger>
                                            <Buttons size="iconSm" color="light" title="Ver">
                                                <i class="fa-solid fa-ellipsis-vertical text-slate-500 dark:text-slate-400"></i>
                                            </Buttons>
                                        </template>
                                        <template #content>
                                            <div class="px-2 py-1 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-700 mb-1">
                                                Acciones
                                            </div>
                                            <DropdownLink :href="route(rutaPrefijo + '-show', { id: item.id })" class="flex items-center gap-2">
                                                <i class="fa-light fa-eye text-gray-700 dark:text-slate-300"></i>
                                                <span class="dark:text-slate-200">Ver Ficha</span>
                                            </DropdownLink>
                                        </template>
                                    </Dropdown>
                                </div>
                            </template>
                        </TableFilter>
                    </div>
                </div>
                <div v-else>
                    <CardBoxComponentEmpty />
                </div>

            </div>
        </div>
    </BreezeAuthenticatedLayout>
</template>
